import {
  Box,
  Card,
  CardContent,
  Grid,
  Stack,
  Typography,
  LinearProgress,
  Button,
} from '@mui/material';
import { useEffect, useState, useCallback } from 'react';
import { useTranslate } from 'react-admin';
import { DashboardContent } from '../../../../../layouts/dashboard';
import { CustomBreadcrumbs } from '../../../../../components/custom-breadcrumbs';
import { Resources } from '../../resource';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import {
  getRedPacketStats,
  getDashboardStats,
  getRedPacketTimeSeriesStats,
  RedPacketStats,
  RedPacketTimeSeriesStats,
  DashboardStats,
  getTradingTimeSeriesStats,
  getDepositTimeSeriesStats,
  TradingTimeSeriesResponse,
  DepositTimeSeriesResponse,
  getPostCampaignCleanupStats,
  PostCampaignCleanupStats,
} from '../../../services/redpacketService';
import ReactApexChart from 'react-apexcharts';
import { useChart } from '../../../../../components/chart/use-chart';
import { fNumber, fShortenNumber } from '../../../../../utils/format-number';
import moment from 'moment';
import '../dataProvider';

const StatCard = ({
  title,
  subtitle0,
  subtitle1,
  subtitle2,
  subtitle3,
  subtitle4,
  consumptionRate,
  consumptionRatioLabel = 'Consumption Ratio',
}: {
  title: string;
  subtitle0: string;
  subtitle1?: string;
  subtitle2?: string;
  subtitle3?: string;
  subtitle4?: string;
  consumptionRate?: string;
  consumptionRatioLabel?: string;
}) => (
  <Card>
    <CardContent>
      <Typography variant="subtitle2" color="text.secondary">
        {title}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.2 }}>
        {subtitle0}
      </Typography>
      {subtitle1 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.2 }}>
          {subtitle1}
        </Typography>
      )}
      {subtitle2 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.2 }}>
          {subtitle2}
        </Typography>
      )}
      {subtitle3 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.2 }}>
          {subtitle3}
        </Typography>
      )}
      {subtitle4 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.2 }}>
          {subtitle4}
        </Typography>
      )}
      {consumptionRate && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            {consumptionRatioLabel}: {consumptionRate}%
          </Typography>
          <LinearProgress
            variant="determinate"
            value={parseFloat(consumptionRate)}
            sx={{ height: 6, borderRadius: 3 }}
          />
        </Box>
      )}
    </CardContent>
  </Card>
);

export default function RedPacketOverview() {
  const t = useTranslate();
  // const [stats, setStats] = useState<RedPacketStats | null>(null);
  const [dashboardData, setDashboardData] = useState<DashboardStats | null>(null);
  const [ttimeSeriesStats, settTimeSeriesStats] = useState<TradingTimeSeriesResponse | null>(null);
  const [dtimeSeriesStats, setdTimeSeriesStats] = useState<DepositTimeSeriesResponse | null>(null);
  const [depositLoading, setDepositLoading] = useState(false);
  const [tradingLoading, setTradingLoading] = useState(false);
  const [depositDateRange, setDepositDateRange] = useState<{ start?: string; end?: string }>({});
  const [tradingDateRange, setTradingDateRange] = useState<{ start?: string; end?: string }>({});
  const [cleanupStats, setCleanupStats] = useState<PostCampaignCleanupStats | null>(null);

  // 初始加载：获取 dashboard 数据
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const dashboardDataResult = await getDashboardStats();
        setDashboardData(dashboardDataResult);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      }
    };
    fetchDashboardData();
  }, []);

  // 初始加载：获取活动结束后的清理统计
  useEffect(() => {
    const fetchCleanupStats = async () => {
      try {
        const cleanupStatsResult = await getPostCampaignCleanupStats();
        setCleanupStats(cleanupStatsResult);
      } catch (error) {
        console.error('Failed to fetch cleanup stats:', error);
      }
    };
    fetchCleanupStats();
  }, []);

  // 验证日期：startDate 和 endDate 必须都是 null 或都不是 null，且 endDate 不能超过当前时间
  const validateDates = (startDate: Dayjs | null, endDate: Dayjs | null): boolean => {
    const bothNull = !startDate && !endDate;
    const bothNotNull = startDate && endDate;

    if (!bothNull && !bothNotNull) {
      return false; // 必须都是 null 或都不是 null
    }

    if (bothNotNull && startDate && endDate) {
      const now = dayjs();
      if (endDate.isAfter(now, 'day')) {
        return false; // endDate 不能超过当前时间
      }
      if (startDate.isAfter(endDate, 'day')) {
        return false; // startDate 不能晚于 endDate
      }
    }

    return true;
  };

  // 获取充值图表数据
  const fetchDepositData = useCallback(async (startDate?: Dayjs | null, endDate?: Dayjs | null) => {
    // 验证日期
    if (!validateDates(startDate || null, endDate || null)) {
      return; // 验证失败，不发送请求
    }

    try {
      setDepositLoading(true);
      // 直接传递 Dayjs 对象，服务层会处理转换
      const dtimeSeriesData = await getDepositTimeSeriesStats(
        startDate || undefined,
        endDate || undefined
      );
      setdTimeSeriesStats(dtimeSeriesData);

      // 从返回的数据中提取日期范围
      if (dtimeSeriesData?.daily && dtimeSeriesData.daily.length > 0) {
        const dates = dtimeSeriesData.daily.map((item) => item.date).sort();
        setDepositDateRange({
          start: dates[0],
          end: dates[dates.length - 1],
        });
      } else {
        setDepositDateRange({});
      }

      console.log('dTimeSeriesStats:', dtimeSeriesData);
    } catch (error) {
      console.error('Failed to fetch deposit data:', error);
    } finally {
      setDepositLoading(false);
    }
  }, []);

  // 获取交易图表数据
  const fetchTradingData = useCallback(async (startDate?: Dayjs | null, endDate?: Dayjs | null) => {
    // 验证日期
    if (!validateDates(startDate || null, endDate || null)) {
      return; // 验证失败，不发送请求
    }

    try {
      setTradingLoading(true);
      // 直接传递 Dayjs 对象，服务层会处理转换
      const ttimeSeriesData = await getTradingTimeSeriesStats(
        startDate || undefined,
        endDate || undefined
      );
      settTimeSeriesStats(ttimeSeriesData);

      // 从返回的数据中提取日期范围
      if (ttimeSeriesData?.daily && ttimeSeriesData.daily.length > 0) {
        const dates = ttimeSeriesData.daily.map((item) => item.date).sort();
        setTradingDateRange({
          start: dates[0],
          end: dates[dates.length - 1],
        });
      } else {
        setTradingDateRange({});
      }

      console.log('tTimeSeriesStats:', ttimeSeriesData);
    } catch (error) {
      console.error('Failed to fetch trading data:', error);
    } finally {
      setTradingLoading(false);
    }
  }, []);

  // 初始加载：获取两个图表的数据（不传日期参数）
  useEffect(() => {
    fetchDepositData();
    fetchTradingData();
  }, [fetchDepositData, fetchTradingData]);

  // 从 dashboardData 获取共享资金池数据
  const sharedPool = dashboardData?.sharedPool;
  const sharedPoolTotal = sharedPool ? (sharedPool.totalBudget || '0') : 0;
  const sharedPoolConsumed = sharedPool ? (sharedPool.spentBalance || '0') : 0;
  const sharedPoolRemaining = sharedPool ? (sharedPool.availableBalance || '0') : 0;
  const sharedPoolConsumptionRate = sharedPool?.spentPercentage || '0.00';

  // 从 dashboardData 获取暴击资金池数据
  const criticalHitPool = dashboardData?.criticalHitPool;
  const criticalHitTotal = criticalHitPool ? (criticalHitPool.totalBudget || '0') : 0;
  const criticalHitConsumed = criticalHitPool ? (criticalHitPool.spentBalance || '0') : 0;
  const criticalHitRemaining = criticalHitPool
    ? (criticalHitPool.availableBalance || '0')
    : 0;
  const criticalHitConsumptionRate = criticalHitPool?.spentPercentage || '0.00';

  // 从 dashboardData 获取暴击资金池数据 criticalHitDesignatedStats
  const criticalHitDesignatedPool = dashboardData?.criticalHitDesignatedPool;
  const criticalHitDesignatedTotal = criticalHitDesignatedPool
    ? (criticalHitDesignatedPool.totalBudget || '0')
    : 0;
  const criticalHitDesignatedConsumed = criticalHitDesignatedPool
    ? (criticalHitDesignatedPool.spentBalance || '0')
    : 0;
  const criticalHitDesignatedRemaining = criticalHitDesignatedPool
    ? (criticalHitDesignatedPool.availableBalance || '0')
    : 0;
  const criticalHitDesignatedConsumptionRate = criticalHitDesignatedPool?.spentPercentage || '0.00';

  // 从 dashboardData 获取各类型红包统计数据
  const twitterBindingStats = dashboardData?.twitterBindingStats;
  const twitterFollowStats = dashboardData?.twitterFollowStats;
  const depositStats = dashboardData?.depositStats;
  const tradingStats = dashboardData?.tradingStats;

  // 准备图表数据
  // 获取充值图表的所有日期
  const depositDates =
    dtimeSeriesStats?.daily?.map((item) => moment(item.date).format('MM-DD')) || [];
  // 获取交易图表的所有日期
  const tradingDates =
    ttimeSeriesStats?.daily?.map((item) => moment(item.date).format('MM-DD')) || [];

  // @ts-ignore
  const baseChartOptions = useChart({
    chart: {
      type: 'line',
      toolbar: { show: false },
    },
    xaxis: {
      categories: depositDates.length > 0 ? depositDates : ['01-01', '01-02', '01-03'],
    },
    yaxis: [
      {
        title: {
          text: t('module.redpacket.dailyChartLabel'),
        },
        labels: {
          formatter: (value: number) => fNumber(value),
        },
      },
      {
        opposite: true,
        title: {
          text: t('module.redpacket.cumulativeChartLabel'),
        },
        labels: {
          formatter: (value: number) =>  fNumber(value),
        },
      },
    ],
    legend: {
      position: 'top',
      // markers: {
      //   radius: 4,
      // },
    },
    stroke: {
      width: 2,
    },
    plotOptions: {
      bar: {
        columnWidth: '60%',
      },
    },
    dataLabels: {
      enabled: false,
    },
  });

  // 准备充值图表数据 - 确保数据按日期对齐
  const depositDailyItems = dtimeSeriesStats?.daily || [];

  const depositDailyData = depositDailyItems.map((item) => parseFloat(item.depositAmount || '0'));
  const depositCumulativeData = depositDailyItems.map((item) =>
    parseFloat(item.cumulativeDepositAmount || '0')
  );

  console.log('Deposit Daily Data:', depositDailyData);
  console.log('Deposit Cumulative Data:', depositCumulativeData);

  const depositChartData = {
    series: [
      {
        name: t('module.redpacket.dailySeriesName'),
        type: 'column',
        data: depositDailyData,
      },
      {
        name: t('module.redpacket.cumulativeSeriesName'),
        type: 'line',
        data: depositCumulativeData,
      },
    ],
  };

  // 准备交易图表数据 - 确保数据按日期对齐
  const tradingDailyItems = ttimeSeriesStats?.daily || [];

  const tradingDailyData = tradingDailyItems.map((item) => parseFloat(item.tradingAmount || '0'));
  const tradingCumulativeData = tradingDailyItems.map((item) =>
    parseFloat(item.cumulativeTradingAmount || '0')
  );

  console.log('Trading Daily Data:', tradingDailyData);
  console.log('Trading Cumulative Data:', tradingCumulativeData);

  const tradingChartData = {
    series: [
      {
        name: t('module.redpacket.dailySeriesName'),
        type: 'column',
        data: tradingDailyData,
      },
      {
        name: t('module.redpacket.cumulativeSeriesName'),
        type: 'line',
        data: tradingCumulativeData,
      },
    ],
  };

  return (
    <DashboardContent>
      <CustomBreadcrumbs
        heading={t('module.redpacket.overview')}
        links={[
          { name: t('module.redpacket.redPacketManagement'), href: `/${Resources.redpacket}` },
          { name: t('module.redpacket.overview') },
        ]}
      />

      <Grid container spacing={3} sx={{ mt: 2 }}>
        {/* Shared Pool Section */}
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t('module.redpacket.sharedPool')}
          </Typography>
        </Grid>

        {/* 奖金池 */}
        <Grid item xs={12} md={3}>
          <StatCard
            title={t('module.redpacket.prizePool')}
            subtitle0={`${t('module.redpacket.totalLabel')}: $${(sharedPoolTotal)} (100%)`}
            subtitle1={`${t('module.redpacket.consumedLabel')}: $${(sharedPoolConsumed)} (${sharedPoolConsumptionRate}%)`}
            subtitle2={`${t('module.redpacket.remainingLabel')}: $${(sharedPoolRemaining)} (${sharedPool?.remainingPercentage || '100'}%)`}
            consumptionRate={sharedPoolConsumptionRate}
            consumptionRatioLabel={t('module.redpacket.consumptionRatio')}
          />
        </Grid>

        {/* 注册红包 */}
        <Grid item xs={12} md={3}>
          <StatCard
            title={t('module.redpacket.twitterBindingRedPackets')}
            subtitle0={`${t('module.redpacket.unOpenedAmountCount')}: $${((twitterBindingStats?.unopenedAmount || '0'))} (${twitterBindingStats?.unopenedCount || 0})`}
            subtitle1={`${t('module.redpacket.distributedAmountCount')}: $${((twitterBindingStats?.issuedAmount || '0'))} (${twitterBindingStats?.issuedCount || 0})`}
            consumptionRate={
              twitterBindingStats &&
              (parseFloat(twitterBindingStats.consumptionRate) * 100).toFixed(0)
            }
            consumptionRatioLabel={t('module.redpacket.consumptionRatio')}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <StatCard
            title={t('module.redpacket.twitterFollowRedPackets')}
            subtitle0={`${t('module.redpacket.unOpenedAmountCount')}: $${((twitterFollowStats?.unopenedAmount || '0'))} (${twitterFollowStats?.unopenedCount || 0})`}
            subtitle1={`${t('module.redpacket.distributedAmountCount')}: $${((twitterFollowStats?.issuedAmount || '0'))} (${twitterFollowStats?.issuedCount || 0})`}
            consumptionRate={
              twitterFollowStats &&
              (parseFloat(twitterFollowStats.consumptionRate) * 100).toFixed(0)
            }
            consumptionRatioLabel={t('module.redpacket.consumptionRatio')}
          />
        </Grid>

        {/* 充值红包 */}
        <Grid item xs={12} md={3}>
          <StatCard
            title={t('module.redpacket.depositRedPackets')}
            subtitle0={`${t('module.redpacket.unOpenedAmountCount')}: $${((depositStats?.unopenedAmount || '0'))} (${depositStats?.unopenedCount || 0})`}
            subtitle1={`${t('module.redpacket.distributedAmountCount')}: $${((depositStats?.issuedAmount || '0'))} (${depositStats?.issuedCount || 0})`}
            consumptionRate={
              depositStats && (parseFloat(depositStats.consumptionRate) * 100).toFixed(0)
            }
            consumptionRatioLabel={t('module.redpacket.consumptionRatio')}
          />
        </Grid>

        {/* 交易红包 */}
        <Grid item xs={12} md={3}>
          <StatCard
            title={t('module.redpacket.tradingRedPackets')}
            subtitle0={`${t('module.redpacket.unOpenedAmountCount')}: $${((tradingStats?.unopenedAmount || '0'))} (${tradingStats?.unopenedCount || 0})`}
            subtitle1={`${t('module.redpacket.distributedAmountCount')}: $${((tradingStats?.issuedAmount || '0'))} (${tradingStats?.issuedCount || 0})`}
            consumptionRate={
              tradingStats && (parseFloat(tradingStats.consumptionRate) * 100).toFixed(0)
            }
            consumptionRatioLabel={t('module.redpacket.consumptionRatio')}
          />
        </Grid>

        {/* Critical Hit Pool Section */}
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t('module.redpacket.criticalHitPool')}
          </Typography>
        </Grid>

        {/* 暴击资金池 */}
        <Grid item xs={12} md={3}>
          <StatCard
            title={t('module.redpacket.criticalHitPool')}
            subtitle0={`${t('module.redpacket.totalLabel')}: $${(criticalHitTotal)} (100%)`}
            subtitle1={`${t('module.redpacket.consumedLabel')}: $${(criticalHitConsumed)} (${criticalHitConsumptionRate}%)`}
            subtitle2={`${t('module.redpacket.remainingLabel')}: $${(criticalHitRemaining)} (${criticalHitPool?.remainingPercentage || '100'}%)`}
            consumptionRate={criticalHitConsumptionRate}
            consumptionRatioLabel={t('module.redpacket.consumptionRatio')}
          />
        </Grid>
        {/* Critical Hit Designated Pool Section */}
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t('module.redpacket.criticalHitDesignatedPool')}
          </Typography>
        </Grid>

        {/* 暴击资金池 design */}
        <Grid item xs={12} md={3}>
          <StatCard
            title={t('module.redpacket.criticalHitDesignatedPool')}
            subtitle0={`${t('module.redpacket.totalLabel')}: $${criticalHitDesignatedTotal} (100%)`}
            subtitle1={`${t('module.redpacket.consumedLabel')}: $${(criticalHitDesignatedConsumed)} (${criticalHitDesignatedConsumptionRate}%)`}
            subtitle2={`${t('module.redpacket.remainingLabel')}: $${(criticalHitDesignatedRemaining)} (${criticalHitDesignatedPool?.remainingPercentage || '100'}%)`}
            consumptionRate={criticalHitDesignatedConsumptionRate}
            consumptionRatioLabel={t('module.redpacket.consumptionRatio')}
          />
        </Grid>

        {/* Post Campaign Cleanup Stats Section */}
        {cleanupStats?.latestRun && (
          <>
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {t('module.redpacket.postCampaignCleanupStats')}
              </Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <StatCard
                title={t('module.redpacket.sharedPoolRecovered')}
                subtitle0={`${t('module.redpacket.amountLabel')}: $${cleanupStats.latestRun.sharedPoolRecovered || '0'}`}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <StatCard
                title={t('module.redpacket.criticalHitPoolRecovered')}
                subtitle0={`${t('module.redpacket.amountLabel')}: $${cleanupStats.latestRun.criticalHitPoolRecovered || '0'}`}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <StatCard
                title={t('module.redpacket.designatedPoolRecovered')}
                subtitle0={`${t('module.redpacket.amountLabel')}: $${cleanupStats.latestRun.designatedPoolRecovered || '0'}`}
              />
            </Grid>
          </>
        )}

        {/* 充值金额统计图表 */}
        <Grid item xs={12} md={12}>
          <Card>
            <CardContent>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 2 }}
              >
                <Typography variant="h6">
                  {t('module.redpacket.depositAmountStatistics')}
                </Typography>
              </Stack>
              {depositDateRange.start && depositDateRange.end && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 2, display: 'block' }}
                >
                  {t('module.redpacket.dateRange')}: {moment(depositDateRange.start).format('YYYY-MM-DD')} {t('common.to')} {' '}
                  {moment(depositDateRange.end).format('YYYY-MM-DD')}
                </Typography>
              )}
              {depositLoading ? (
                <Box
                  sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {t('module.redpacket.loading')}
                  </Typography>
                </Box>
              ) : dtimeSeriesStats &&
                depositDates.length > 0 &&
                (depositDailyData.length > 0 || depositCumulativeData.length > 0) ? (
                <ReactApexChart
                  type="line"
                  series={depositChartData.series}
                  options={{
                    ...baseChartOptions,
                    xaxis: {
                      ...baseChartOptions?.xaxis,
                      categories: depositDates,
                    },
                    chart: {
                      ...baseChartOptions?.chart,
                      type: 'line',
                      stacked: false,
                    },
                    colors: ['#1976d2', '#000000'],
                    stroke: {
                      curve: 'smooth',
                      width: [0, 2],
                    },
                    fill: {
                      type: ['solid', 'solid'],
                      opacity: [1, 1],
                    },
                    tooltip: {
                      shared: true,
                      intersect: false,
                    },
                    markers: {
                      size: [0, 4],
                    },
                    plotOptions: {
                      bar: {
                        columnWidth: '60%',
                      },
                    },
                  }}
                  height={350}
                />
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ textAlign: 'center', py: 4 }}
                >
                  {depositLoading ? t('module.redpacket.loading') : t('module.redpacket.noDataAvailable')}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* 交易金额统计图表 */}
        <Grid item xs={12} md={12}>
          <Card>
            <CardContent>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 2 }}
              >
                <Typography variant="h6">
                  {t('module.redpacket.tradingAmountStatistics')}
                </Typography>
              </Stack>
              {tradingDateRange.start && tradingDateRange.end && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 2, display: 'block' }}
                >
                  {t('module.redpacket.dateRange')}: {moment(tradingDateRange.start).format('YYYY-MM-DD')} {t('common.to')} {' '}
                  {moment(tradingDateRange.end).format('YYYY-MM-DD')}
                </Typography>
              )}
              {tradingLoading ? (
                <Box
                  sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {t('module.redpacket.loading')}
                  </Typography>
                </Box>
              ) : ttimeSeriesStats &&
                tradingDates.length > 0 &&
                (tradingDailyData.length > 0 || tradingCumulativeData.length > 0) ? (
                <ReactApexChart
                  type="line"
                  series={tradingChartData.series}
                  options={{
                    ...baseChartOptions,
                    xaxis: {
                      ...baseChartOptions?.xaxis,
                      categories: tradingDates,
                    },
                    chart: {
                      ...baseChartOptions?.chart,
                      type: 'line',
                      stacked: false,
                    },
                    colors: ['#1976d2', '#000000'],
                    stroke: {
                      curve: 'smooth',
                      width: [0, 2],
                    },
                    fill: {
                      type: ['solid', 'solid'],
                      opacity: [1, 1],
                    },
                    tooltip: {
                      shared: true,
                      intersect: false,
                    },
                    markers: {
                      size: [0, 4],
                    },
                    plotOptions: {
                      bar: {
                        columnWidth: '60%',
                      },
                    },
                  }}
                  height={350}
                />
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ textAlign: 'center', py: 4 }}
                >
                  {tradingLoading ? t('module.redpacket.loading') : t('module.redpacket.noDataAvailable')}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </DashboardContent>
  );
}
